import type { ModuleRecord, ModuleManifest } from "@arclog/module-sdk";
import { fits, firstFree, nearestFit, layoutForWidth } from "./desktop-grid-geometry.mjs";
import { extensionOf, resolveAppForPath } from "./file-assoc.mjs";
import { reorderPins } from "./dock-drop.mjs";
import {
  SCOPED_KEYS,
  migrateLegacyKeys,
  scopeIdFromToken,
  scopedKey,
} from "./user-scope.mjs";
import { trackEvent } from "./analytics";

function safeParse<T>(key: string, fallback: T): T {
  try {
    const v = JSON.parse(localStorage.getItem(key) || "null");
    return v == null ? fallback : (v as T);
  } catch {
    return fallback;
  }
}

/**
 * Which account's persisted state this tab is looking at. Everything in
 * SCOPED_KEYS is read and written through `scopedKey(base, bootScope)` — see
 * user-scope.mjs for why namespacing beats clearing.
 *
 * Computed at module load, BEFORE the store is constructed, because the class
 * field initialisers below read storage immediately.
 */
const bootScope = scopeIdFromToken(localStorage.getItem("arclog.token"));
try {
  migrateLegacyKeys(localStorage, bootScope, SCOPED_KEYS);
} catch {
  /* storage disabled — the OS still boots, just without a saved layout */
}

const DEFAULT_DOCK_PINS = [
  "app.files",
  "app.terminal",
  "app.code",
  "app.notepad",
  "app.settings",
  "app.wallpaper",
];

export type DesktopItemType = "widget" | "icon";
export interface DesktopItem {
  id: string; // "it_<n>" — deterministic per session
  type: DesktopItemType;
  moduleId: string; // "widget.clock" | "app.files" | ...
  col: number;
  row: number;
  cols: number;
  rows: number;
}
export interface PanelItem {
  id: string;
  moduleId: string;
}

export type OsWindow = {
  id: string;
  appId: string;
  version?: string;
  /**
   * True only when the caller asked for a specific version (Packages → "run
   * this version"). A window opened normally records whatever the default was
   * at the time, which must NOT survive a refresh — otherwise the session pins
   * a stale build forever and app updates silently never appear.
   */
  pinned?: boolean;
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minimized: boolean;
  maximized: boolean;
  z: number;
  /**
   * Launch params this window was opened with, surfaced to the app as
   * `ctx.launchParams`. `{ path }` is the one the OS itself sets, via
   * ctx.shell.openPath; anything else is between the caller and the app.
   *
   * Deliberately NOT persisted in the session layout: reopening yesterday's
   * windows should restore geometry, not re-run yesterday's open intent.
   */
  params?: Record<string, unknown>;
};

type Listener = () => void;

class OsStore {
  token: string | null = localStorage.getItem("arclog.token");
  /**
   * Namespace for every persisted key in SCOPED_KEYS. Reassigned by setToken
   * whenever the account changes, which re-points all of them at once.
   */
  private scopeId: string = bootScope;
  /**
   * The CURRENT account's storage scope (see user-scope.mjs). Public read for
   * `createModuleContext`: ctx.appState namespaces must be per-account so one
   * account's app data never surfaces for another in the same browser.
   */
  get accountScope(): string {
    return this.scopeId;
  }
  permissions: string[] = safeParse<string[]>(scopedKey("arclog.perms", bootScope), []);
  packages: ModuleRecord[] = [];
  windows: OsWindow[] = [];
  focusId: string | null = null;
  themeId = localStorage.getItem("arclog.theme") || "theme.macos-glass";
  wallpaperId = localStorage.getItem("arclog.wallpaper") || "wallpaper.starfall";
  wallpaperPaused = false;
  uiScale: number = Number(localStorage.getItem("arclog.uiScale") || "0.9") || 0.9;
  gfxBackend: string = localStorage.getItem("arclog.gfx.backend") || "three";
  /** Pinned app ids for the dock (catalog is Files/Applications/Spotlight). */
  dockPins: string[] = safeParse<string[] | null>(
    scopedKey("arclog.dockPins", bootScope),
    null
  ) || [...DEFAULT_DOCK_PINS];
  toasts: Array<{ id: string; message: string; kind: string }> = [];
  zCounter = 10;
  desktopItems: DesktopItem[] = safeParse<DesktopItem[]>(
    scopedKey("arclog.desktop.items", bootScope),
    []
  );
  panelItems: PanelItem[] = safeParse<PanelItem[]>(
    scopedKey("arclog.panel.items", bootScope),
    []
  );
  /**
   * Widget settings keyed by PLACEMENT id (not module id) so two Clocks on the
   * desktop can differ. Pruned when the placement is removed — see
   * removeDesktopItem / removePanelItem.
   */
  widgetSettings: Record<string, Record<string, unknown>> = safeParse<
    Record<string, Record<string, unknown>>
  >(scopedKey("arclog.widget.settings", bootScope), {});
  /**
   * Wallpaper settings keyed by MODULE ID. Unlike widgets there is exactly one
   * active wallpaper, so there is no placement to key by — and values must
   * survive switching away to another wallpaper and back.
   *
   * Note `wallpaperId` above is an UNSCOPED key while these are scoped per
   * user, matching widgetSettings. That inconsistency predates this and is not
   * fixed here.
   */
  wallpaperSettings: Record<string, Record<string, unknown>> = safeParse<
    Record<string, Record<string, unknown>>
  >(scopedKey("arclog.wallpaper.settings", bootScope), {});
  /**
   * Bumped when a `reload: true` setting is committed. WallpaperLayer folds
   * this into its React key, so the wallpaper remounts with the new bag —
   * values a module cannot apply in place never reach it via onChange.
   */
  wallpaperReloadNonce = 0;
  panelOpen = false;
  private itemSeq = 0;
  // Sentinel 0 (not 12) so the FIRST setGridCols from DesktopGrid always differs
  // and runs the load-time clamp pass, even when the real width is also 12.
  // gridCols() falls back to 12 via `|| 12` for any pre-mount reader.
  private _gridCols = 0;
  private listeners = new Set<Listener>();
  private toastTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor() {
    // Seed the id sequence so item ids stay unique across reloads. nextItemId()
    // is shared by BOTH desktop and panel items, so seed from BOTH arrays —
    // otherwise a reload can hand out an id that already exists on the other
    // surface (duplicate ids break React keys and id-based removal).
    const idNum = (it: { id: string }) => Number(String(it.id).replace("it_", "")) || 0;
    this.itemSeq = [...this.desktopItems, ...this.panelItems].reduce(
      (m, it) => Math.max(m, idNum(it)),
      0
    );
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private emit() {
    for (const l of this.listeners) l();
  }

  /** Public notify for hosts (theme/wallpaper) after external state change */
  notifyListeners() {
    this.emit();
  }

  dismissToast(id: string) {
    const t = this.toastTimers.get(id);
    if (t) clearTimeout(t);
    this.toastTimers.delete(id);
    const before = this.toasts.length;
    this.toasts = this.toasts.filter((x) => x.id !== id);
    if (this.toasts.length !== before) this.emit();
  }

  /** Storage key for `base` in the account currently signed in. */
  private k(base: string): string {
    return scopedKey(base, this.scopeId);
  }

  /**
   * Re-read every scoped key after the account changed. Nothing is written
   * here: the outgoing account's state stays exactly where it was, under its
   * own namespace, ready for the next time they sign in.
   */
  private loadScopedState() {
    this.permissions = safeParse<string[]>(this.k("arclog.perms"), []);
    this.dockPins = safeParse<string[] | null>(this.k("arclog.dockPins"), null) || [
      ...DEFAULT_DOCK_PINS,
    ];
    this.desktopItems = safeParse<DesktopItem[]>(this.k("arclog.desktop.items"), []);
    this.panelItems = safeParse<PanelItem[]>(this.k("arclog.panel.items"), []);
    this.widgetSettings = safeParse<Record<string, Record<string, unknown>>>(
      this.k("arclog.widget.settings"),
      {}
    );
    this.wallpaperSettings = safeParse<Record<string, Record<string, unknown>>>(
      this.k("arclog.wallpaper.settings"),
      {}
    );
    // Placement ids are handed out from a single counter shared by both
    // surfaces — reseed it or the new account's first new tile collides with a
    // restored one. Same reasoning as the constructor.
    const idNum = (it: { id: string }) => Number(String(it.id).replace("it_", "")) || 0;
    this.itemSeq = [...this.desktopItems, ...this.panelItems].reduce(
      (m, it) => Math.max(m, idNum(it)),
      0
    );
  }

  setToken(token: string | null) {
    this.token = token;
    // An account change re-points every persisted key. Without this the desktop
    // an admin arranged — including icons for apps this account may not even
    // see — carried straight into the next session in the same browser, and the
    // incoming account started life holding the previous one's cached perms.
    const nextScope = scopeIdFromToken(token);
    const switched = nextScope !== this.scopeId;
    if (switched) {
      this.scopeId = nextScope;
      this.loadScopedState();
      // Package list belongs to the old account until the new fetch lands.
      this.packages = [];
    }
    if (token) {
      localStorage.setItem("arclog.token", token);
      // Anything left at a pre-namespacing key belongs to whoever signed in
      // first after the upgrade; claim it now rather than leaving it for a
      // later, different account to inherit.
      try {
        migrateLegacyKeys(localStorage, nextScope, SCOPED_KEYS);
      } catch {
        /* */
      }
      if (switched) this.loadScopedState();
    } else {
      localStorage.removeItem("arclog.token");
      // Identity is not layout: drop the cached display user too, or the login
      // screen and presence beacon keep naming the account that just left.
      localStorage.removeItem("arclog.user");
      // Drop open desk layout on logout so the next sign-in starts clean
      this.windows = [];
      this.focusId = null;
      this.clearSessionLayout();
    }
    this.emit();
  }

  setPermissions(perms: string[]) {
    this.permissions = perms;
    localStorage.setItem(this.k("arclog.perms"), JSON.stringify(perms));
    this.emit();
  }

  setPackages(packages: ModuleRecord[]) {
    this.packages = packages;
    this.emit();
  }

  notify(message: string, kind: "info" | "success" | "error" | "warn" = "info") {
    const id = crypto.randomUUID();
    // Cap stack so spam doesn't cover the desktop
    const next = [...this.toasts, { id, message, kind }];
    while (next.length > 5) {
      const dropped = next.shift();
      if (dropped) {
        const old = this.toastTimers.get(dropped.id);
        if (old) clearTimeout(old);
        this.toastTimers.delete(dropped.id);
      }
    }
    this.toasts = next;
    this.emit();
    const ms = kind === "error" ? 5200 : kind === "warn" ? 4200 : 3200;
    const timer = setTimeout(() => this.dismissToast(id), ms);
    this.toastTimers.set(id, timer);
  }

  private winStateKey(appId: string, version?: string) {
    return `arclog.winstate.${appId}${version ? "@" + version : ""}`;
  }

  private loadWinState(appId: string, version?: string): Partial<OsWindow> | null {
    try {
      const raw = localStorage.getItem(this.winStateKey(appId, version));
      if (!raw) return null;
      const s = JSON.parse(raw) as Partial<OsWindow>;
      if (typeof s.x !== "number" || typeof s.w !== "number") return null;
      return s;
    } catch {
      return null;
    }
  }

  /** Persist geometry when user moves/resizes/maximizes/closes a window */
  saveWinState(win: OsWindow) {
    try {
      const payload = {
        x: win.x,
        y: win.y,
        w: win.w,
        h: win.h,
        maximized: !!win.maximized,
      };
      localStorage.setItem(this.winStateKey(win.appId, win.version), JSON.stringify(payload));
    } catch {
      /* */
    }
  }

  /**
   * First-open placement: center in the desktop (below menubar, above dock).
   * Light cascade when several new windows open so they don't fully stack.
   */
  private defaultWindowPosition(
    w: number,
    h: number,
    cascadeIndex = 0
  ): { x: number; y: number } {
    const menubar = 28; // --os-menubar-h
    const dockReserve = 96; // dock icons + pad + breathing room
    const margin = 12;
    const vw =
      typeof window !== "undefined" && window.innerWidth
        ? window.innerWidth
        : 1280;
    const vh =
      typeof window !== "undefined" && window.innerHeight
        ? window.innerHeight
        : 800;
    const availTop = menubar + margin;
    const availBottom = Math.max(availTop + 120, vh - dockReserve);
    const availH = availBottom - availTop;
    const step = cascadeIndex % 8;
    let x = Math.round((vw - w) / 2) + step * 22;
    let y = Math.round(availTop + (availH - h) / 2) + step * 18;
    const maxX = Math.max(margin, vw - Math.min(w, vw - margin * 2) - margin);
    const maxY = Math.max(availTop, availBottom - Math.min(h, availH));
    x = Math.min(Math.max(margin, x), maxX);
    y = Math.min(Math.max(availTop, y), maxY);
    return { x, y };
  }

  /**
   * Launch-param subscribers, keyed by window id. Only fires when an
   * ALREADY-OPEN window is re-launched with new params — a fresh window reads
   * its params from `ctx.launchParams.get()` at mount and never needs this.
   */
  private launchParamsListeners = new Map<
    string,
    Set<(params: Record<string, unknown>) => void>
  >();

  /** ctx.launchParams.subscribe — returns a real unsubscribe. */
  subscribeLaunchParams(
    windowId: string,
    cb: (params: Record<string, unknown>) => void
  ): () => void {
    let set = this.launchParamsListeners.get(windowId);
    if (!set) {
      set = new Set();
      this.launchParamsListeners.set(windowId, set);
    }
    set.add(cb);
    return () => {
      const s = this.launchParamsListeners.get(windowId);
      if (!s) return;
      s.delete(cb);
      if (s.size === 0) this.launchParamsListeners.delete(windowId);
    };
  }

  /**
   * Re-point an open window at new launch params and tell the app.
   *
   * This is what makes `edit a.txt` then `edit b.txt` work on a SINGLETON app:
   * openApp focuses the existing window instead of creating one, so without
   * this the second command would silently do nothing at all.
   */
  private applyLaunchParams(windowId: string, params?: Record<string, unknown>) {
    if (!params) return;
    this.updateWindow(windowId, { params });
    const subs = this.launchParamsListeners.get(windowId);
    if (!subs) return;
    // Snapshot: a callback may unsubscribe itself (an app switching documents).
    for (const cb of [...subs]) {
      try {
        cb(params);
      } catch (e) {
        // One badly-behaved app must not stop the others from being told.
        console.error("[osStore] launchParams subscriber threw", e);
      }
    }
  }

  openApp(
    appId: string,
    title?: string,
    version?: string,
    params?: Record<string, unknown>
  ) {
    const pkgEarly = this.packages.find((p) => p.manifest.id === appId || p.id === appId);

    // Every app launch in the OS funnels through here — dock, desktop icon,
    // Spotlight, terminal, session restore — so this single call is the whole
    // of "what did they use". Recorded before the visibility check so a refused
    // launch still shows up as an attempt; `trackEvent` never throws.
    trackEvent("app.open", { appId, props: { version: version || null } });

    // UX hygiene, NOT a security boundary. `this.packages` is already the
    // server-filtered list (routes/api.ts packagesRouter applies
    // packageVisibleToRole), and the real boundaries are requireAgentAccess and
    // the OpenRouter key resolver — both server-side, both unaffected by
    // anything done here. Opening a window onto an app whose backend refuses
    // you gains nothing; this only avoids the confusing dead pane, and says why.
    //
    // Only enforced once packages have actually loaded: an empty list means the
    // fetch hasn't landed yet, and refusing then would break session restore.
    if (this.packages.length && !pkgEarly) {
      this.notify(`${title || appId} isn't available for your account`, "warn");
      return "";
    }

    const verEarly = version || pkgEarly?.defaultVersion;
    const winDefEarly =
      (verEarly && pkgEarly?.versions?.[verEarly]?.manifest.windows?.[0]) ||
      pkgEarly?.manifest.windows?.[0];
    // Default true for stability; apps may set windows[0].singleton: false (e.g. Notepad).
    const singleton = winDefEarly?.singleton !== false;

    if (singleton) {
      const existing = this.windows.find(
        (w) =>
          w.appId === appId &&
          (version ? w.version === version : true) &&
          !w.minimized
      );
      if (existing) {
        this.focus(existing.id);
        this.applyLaunchParams(existing.id, params);
        // Let already-mounted apps pick up pending open intents (sessionStorage, etc).
        try {
          window.dispatchEvent(
            new CustomEvent("arclog:app-activate", {
              detail: { appId, windowId: existing.id, version: existing.version },
            })
          );
        } catch {
          /* */
        }
        return existing.id;
      }
      const minimized = this.windows.find(
        (w) =>
          w.appId === appId &&
          (version ? w.version === version : true) &&
          w.minimized
      );
      if (minimized) {
        this.updateWindow(minimized.id, { minimized: false });
        this.focus(minimized.id);
        this.applyLaunchParams(minimized.id, params);
        try {
          window.dispatchEvent(
            new CustomEvent("arclog:app-activate", {
              detail: { appId, windowId: minimized.id, version: minimized.version },
            })
          );
        } catch {
          /* */
        }
        return minimized.id;
      }
    }
    const pkg = pkgEarly;
    const ver = verEarly;
    const winDef = winDefEarly;
    const saved = this.loadWinState(appId, ver);
    const w = saved?.w ?? winDef?.defaultWidth ?? 720;
    const h = saved?.h ?? winDef?.defaultHeight ?? 440;
    const id = crypto.randomUUID();
    this.zCounter += 1;
    const label = title || pkg?.manifest.name || appId;
    const hasSavedPos =
      typeof saved?.x === "number" && typeof saved?.y === "number";
    const pos = hasSavedPos
      ? { x: saved!.x as number, y: saved!.y as number }
      : this.defaultWindowPosition(w, h, this.windows.length);
    const win: OsWindow = {
      id,
      appId,
      version: ver,
      pinned: !!version,
      title: ver && ver !== pkg?.defaultVersion ? `${label} (${ver})` : label,
      x: pos.x,
      y: pos.y,
      w,
      h,
      minimized: false,
      maximized: !!saved?.maximized,
      z: this.zCounter,
      params,
    };
    this.windows = [...this.windows, win];
    this.focusId = id;
    this.persistSessionLayout();
    this.emit();
    return id;
  }

  /** Launch params a window was opened with — the source for ctx.launchParams.get(). */
  launchParamsFor(windowId?: string): Record<string, unknown> | undefined {
    if (!windowId) return undefined;
    return this.windows.find((w) => w.id === windowId)?.params;
  }

  /**
   * Open `path` in whichever enabled app declares its extension in
   * `manifest.fileTypes`. Backs ctx.shell.openPath.
   *
   * "Nothing handles .xyz" is a NORMAL answer, not an exception — the caller
   * (Terminal, Files) wants to say so in its own words, so this reports rather
   * than throws, and hands back the extension it looked for.
   */
  openPath(path: string): {
    opened: boolean;
    appId?: string;
    windowId?: string;
    extension: string;
  } {
    const extension = extensionOf(path);
    const appId = resolveAppForPath(this.packages, path);
    if (!appId) return { opened: false, extension };
    const windowId = this.openApp(appId, undefined, undefined, { path });
    // openApp returns "" when it refuses (app not visible to this account).
    if (!windowId) return { opened: false, appId, extension };
    return { opened: true, appId, windowId, extension };
  }

  /** Per-window veto, installed by apps via ctx.windows.setCloseGuard. */
  private closeGuards = new Map<string, () => boolean | Promise<boolean>>();

  setCloseGuard(id: string, guard: (() => boolean | Promise<boolean>) | null) {
    if (guard) this.closeGuards.set(id, guard);
    else this.closeGuards.delete(id);
  }

  /**
   * Close a window, asking its guard first. Use this for anything the user
   * initiated (traffic light, menu, ⌘W); `closeWindow` is the unconditional
   * path for teardown.
   */
  async requestCloseWindow(id: string): Promise<boolean> {
    const guard = this.closeGuards.get(id);
    if (guard) {
      let allowed = true;
      try {
        allowed = (await guard()) !== false;
      } catch {
        // A broken guard must never trap a window open.
        allowed = true;
      }
      if (!allowed) return false;
    }
    this.closeWindow(id);
    return true;
  }

  closeWindow(id: string) {
    const win = this.windows.find((w) => w.id === id);
    if (win) this.saveWinState(win);
    this.closeGuards.delete(id);
    // Belt and braces: AppHost disposes launchParams subscriptions on unmount,
    // but the window record is the thing they are keyed to.
    this.launchParamsListeners.delete(id);
    this.windows = this.windows.filter((w) => w.id !== id);
    if (this.focusId === id) this.focusId = this.windows.at(-1)?.id ?? null;
    this.persistSessionLayout();
    this.emit();
  }

  closeAppWindows(appId: string) {
    for (const w of this.windows) {
      if (w.appId !== appId) continue;
      this.closeGuards.delete(w.id);
      this.launchParamsListeners.delete(w.id);
    }
    this.windows = this.windows.filter((w) => w.appId !== appId);
    this.persistSessionLayout();
    this.emit();
  }

  focus(id: string) {
    this.zCounter += 1;
    this.windows = this.windows.map((w) =>
      w.id === id ? { ...w, z: this.zCounter, minimized: false } : w
    );
    this.focusId = id;
    this.persistSessionLayout();
    this.emit();
  }

  updateWindow(id: string, patch: Partial<OsWindow>) {
    this.windows = this.windows.map((w) => {
      if (w.id !== id) return w;
      const next = { ...w, ...patch };
      // Persist geometry-affecting changes (not every z-bump)
      if (
        patch.x != null ||
        patch.y != null ||
        patch.w != null ||
        patch.h != null ||
        patch.maximized != null ||
        patch.minimized != null
      ) {
        this.saveWinState(next);
      }
      return next;
    });
    this.persistSessionLayout();
    this.emit();
  }

  /** sessionStorage layout — survives refresh, cleared on reboot/logout */
  private sessionLayoutKey() {
    return "arclog.session.windows";
  }

  persistSessionLayout() {
    try {
      const payload = {
        v: 1,
        focusId: this.focusId,
        zCounter: this.zCounter,
        windows: this.windows.map((w) => ({
          id: w.id,
          appId: w.appId,
          version: w.version,
          pinned: w.pinned,
          title: w.title,
          x: w.x,
          y: w.y,
          w: w.w,
          h: w.h,
          minimized: !!w.minimized,
          maximized: !!w.maximized,
          z: w.z,
        })),
      };
      sessionStorage.setItem(this.sessionLayoutKey(), JSON.stringify(payload));
    } catch {
      /* */
    }
  }

  /** Restore open windows after browser refresh (same tab session). */
  restoreSessionLayout(): boolean {
    try {
      if (sessionStorage.getItem("arclog.session.reboot") === "1") {
        sessionStorage.removeItem("arclog.session.reboot");
        sessionStorage.removeItem(this.sessionLayoutKey());
        return false;
      }
      const raw = sessionStorage.getItem(this.sessionLayoutKey());
      if (!raw) return false;
      const data = JSON.parse(raw) as {
        windows?: OsWindow[];
        focusId?: string | null;
        zCounter?: number;
      };
      if (!Array.isArray(data.windows) || !data.windows.length) return false;
      this.windows = data.windows.map((w) => ({
        id: w.id || crypto.randomUUID(),
        appId: w.appId,
        // Only an explicitly pinned window keeps its version. Otherwise drop it
        // so AppHost requests /modules/<id>/index.js and the server resolves the
        // current default — a restored window must not run a stale build.
        version: w.pinned ? w.version : undefined,
        pinned: w.pinned,
        title: w.title || w.appId,
        x: Number(w.x) || 80,
        y: Number(w.y) || 60,
        w: Math.max(280, Number(w.w) || 720),
        h: Math.max(180, Number(w.h) || 440),
        minimized: !!w.minimized,
        maximized: !!w.maximized,
        z: Number(w.z) || 10,
      }));
      this.zCounter = Math.max(
        this.zCounter,
        Number(data.zCounter) || 10,
        ...this.windows.map((w) => w.z)
      );
      this.focusId =
        data.focusId && this.windows.some((w) => w.id === data.focusId)
          ? data.focusId
          : this.windows.find((w) => !w.minimized)?.id || this.windows[0]?.id || null;
      this.emit();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Soft reboot: close all apps (free mounts/JS), clear session layout caches,
   * keep auth; next boot is empty desktop unless user opens apps again.
   */
  rebootSession(opts?: { reload?: boolean }) {
    try {
      // Drop app mounts fully
      this.windows = [];
      this.focusId = null;
      this.zCounter = 10;
      this.toasts = [];
      sessionStorage.setItem("arclog.session.reboot", "1");
      sessionStorage.removeItem(this.sessionLayoutKey());
      sessionStorage.removeItem("arclog.openedTerminal");
      sessionStorage.removeItem("arclog.openedFiles");
      // Clear transient app open intents
      for (const k of Object.keys(sessionStorage)) {
        if (
          k.startsWith("arclog.notepad.open") ||
          k.startsWith("arclog.files.open") ||
          k.startsWith("arclog.session.")
        ) {
          if (k !== "arclog.session.reboot") sessionStorage.removeItem(k);
        }
      }
      this.emit();
      if (opts?.reload !== false) {
        window.location.reload();
      }
    } catch {
      window.location.reload();
    }
  }

  clearSessionLayout() {
    try {
      sessionStorage.removeItem(this.sessionLayoutKey());
    } catch {
      /* */
    }
  }

  setTheme(id: string, vars?: Record<string, string>) {
    this.themeId = id;
    localStorage.setItem("arclog.theme", id);
    if (vars && Object.keys(vars).length) {
      const root = document.documentElement;
      for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
    }
    this.emit();
  }

  themeModules(): ModuleManifest[] {
    return this.packages
      .filter((p) => p.state === "enabled" && p.manifest.kind === "theme")
      .map((p) => p.manifest);
  }

  setWallpaper(id: string) {
    this.wallpaperId = id;
    localStorage.setItem("arclog.wallpaper", id);
    this.emit();
  }

  setWallpaperPaused(p: boolean) {
    this.wallpaperPaused = p;
    this.emit();
  }

  applyUiScale(scale?: number) {
    const next = Math.min(1.25, Math.max(0.8, Number(scale ?? this.uiScale) || 0.9));
    this.uiScale = next;
    try {
      localStorage.setItem("arclog.uiScale", String(next));
    } catch {
      /* */
    }
    document.documentElement.style.setProperty("--os-ui-scale", String(next));
    (document.body as HTMLElement).style.zoom = String(next);
    this.emit();
  }

  setUiScale(scale: number) {
    this.applyUiScale(scale);
  }

  setGfxBackend(backend: string) {
    const b = backend === "canvas2d" ? "canvas2d" : "three";
    this.gfxBackend = b;
    try {
      localStorage.setItem("arclog.gfx.backend", b);
    } catch {
      /* */
    }
    this.emit();
  }

  setDockPins(pins: string[]) {
    const seen = new Set<string>();
    const next: string[] = [];
    for (const id of pins) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      next.push(id);
    }
    this.dockPins = next;
    try {
      localStorage.setItem(this.k("arclog.dockPins"), JSON.stringify(next));
    } catch {
      /* */
    }
    this.emit();
  }

  /**
   * Pin an app to the dock. Returns true only when a pin was actually written,
   * so callers can tell "done" from "declined" without re-deriving why.
   *
   * Fails closed on visibility. dockApps() already intersects pins with the
   * server-filtered package list, so an invisible pin would render nothing —
   * but it would still be PERSISTED, and persisted state outlives the reason it
   * was rejected. Refusing at the write is the difference between "you can't
   * see this app" and "you can't see this app yet".
   *
   * Only enforced once packages have loaded, mirroring openApp(): an empty list
   * means the fetch hasn't landed, and refusing then would break restore.
   */
  pinApp(appId: string): boolean {
    if (!appId) return false;
    if (this.dockPins.includes(appId)) return false;
    const served = this.packages.some(
      (p) => p.state === "enabled" && p.manifest.kind === "app" && (p.manifest.id || p.id) === appId
    );
    if (this.packages.length && !served) return false;
    this.setDockPins([...this.dockPins, appId]);
    return true;
  }

  unpinApp(appId: string) {
    this.setDockPins(this.dockPins.filter((id) => id !== appId));
  }

  /**
   * Move an existing pin to `insertIndex`, a GAP index measured against the
   * dock as the user currently sees it (dragged item still in place) — see
   * reorderPins for why that off-by-one has to be resolved in one place.
   *
   * Persists through setDockPins, so it lands in the same per-account scoped
   * `dockPins` key as pin/unpin and survives a reload like any other pin edit.
   *
   * Returns true only when the order actually changed. A drop back onto an
   * item's own slot is the common case during a drag and must not churn
   * localStorage or fire a re-render.
   */
  reorderPin(appId: string, insertIndex: number): boolean {
    const next = reorderPins(this.dockPins, appId, insertIndex);
    if (next === this.dockPins) return false;
    this.setDockPins(next as string[]);
    return true;
  }

  isPinned(appId: string) {
    return this.dockPins.includes(appId);
  }

  /** Enabled wallpaper packages */
  wallpaperModules(): ModuleManifest[] {
    return this.packages
      .filter((p) => p.state === "enabled" && p.manifest.kind === "wallpaper")
      .map((p) => p.manifest);
  }

  /** Enabled widget packages. */
  widgetModules(): ModuleManifest[] {
    return this.packages
      .filter((p) => p.state === "enabled" && p.manifest.kind === "widget")
      .map((p) => p.manifest);
  }

  private persistDesktop() {
    localStorage.setItem(this.k("arclog.desktop.items"), JSON.stringify(this.desktopItems));
  }
  private persistPanel() {
    localStorage.setItem(this.k("arclog.panel.items"), JSON.stringify(this.panelItems));
  }
  private persistWidgetSettings() {
    localStorage.setItem(this.k("arclog.widget.settings"), JSON.stringify(this.widgetSettings));
  }
  private persistWallpaperSettings() {
    localStorage.setItem(
      this.k("arclog.wallpaper.settings"),
      JSON.stringify(this.wallpaperSettings)
    );
  }
  private nextItemId() {
    this.itemSeq += 1;
    return `it_${this.itemSeq}`;
  }

  getWidgetSettings(placementId: string): Record<string, unknown> {
    return this.widgetSettings[placementId] || {};
  }
  setWidgetSetting(placementId: string, key: string, value: unknown) {
    const cur = this.widgetSettings[placementId] || {};
    this.widgetSettings = { ...this.widgetSettings, [placementId]: { ...cur, [key]: value } };
    this.persistWidgetSettings();
    this.emit();
  }
  resetWidgetSettings(placementId: string) {
    // No-op must not emit: this runs on every placement removal, and an empty
    // emit would churn every subscribed widget for nothing.
    if (!this.widgetSettings[placementId]) return;
    const next = { ...this.widgetSettings };
    delete next[placementId];
    this.widgetSettings = next;
    this.persistWidgetSettings();
    this.emit();
  }

  getWallpaperSettings(moduleId: string): Record<string, unknown> {
    return this.wallpaperSettings[moduleId] || {};
  }
  /**
   * `reload` bumps the nonce so WallpaperLayer remounts. Callers pass it only
   * on COMMIT (pointerup/blur) — a slider's onChange fires per pixel of drag,
   * and remounting on that would rebuild the wallpaper across the whole
   * gesture.
   */
  setWallpaperSetting(moduleId: string, key: string, value: unknown, reload = false) {
    const cur = this.wallpaperSettings[moduleId] || {};
    this.wallpaperSettings = {
      ...this.wallpaperSettings,
      [moduleId]: { ...cur, [key]: value },
    };
    this.persistWallpaperSettings();
    if (reload) this.wallpaperReloadNonce += 1;
    this.emit();
  }
  resetWallpaperSettings(moduleId: string) {
    if (!this.wallpaperSettings[moduleId]) return;
    const next = { ...this.wallpaperSettings };
    delete next[moduleId];
    this.wallpaperSettings = next;
    this.persistWallpaperSettings();
    // Reset can drop a reload-marked value, and the module cannot pick that up
    // in place — remount unconditionally rather than tracking which keys moved.
    this.wallpaperReloadNonce += 1;
    this.emit();
  }

  addDesktopItem(type: DesktopItemType, moduleId: string, cols = 1, rows = 1): string {
    const origin = firstFree(this.desktopItems, cols, rows, this.gridCols());
    const item: DesktopItem = {
      id: this.nextItemId(),
      type,
      moduleId,
      col: origin.col,
      row: origin.row,
      cols,
      rows,
    };
    this.desktopItems = [...this.desktopItems, item];
    this.persistDesktop();
    this.emit();
    return item.id;
  }
  moveDesktopItem(id: string, col: number, row: number) {
    const it = this.desktopItems.find((d) => d.id === id);
    if (!it) return;
    // Resolve against the DISPLAYED layout, not raw stored intent: at a narrow
    // width those differ, and colliding with a tile the user cannot see would
    // send the tile somewhere the drop preview never showed.
    const view = this.displayDesktopItems();
    const dest = nearestFit(view, id, col, row, it.cols, it.rows, this.gridCols());
    this.desktopItems = this.desktopItems.map((d) =>
      d.id === id ? { ...d, col: dest.col, row: dest.row } : d
    );
    this.persistDesktop();
    this.emit();
  }
  /**
   * Resize a tile, deciding placement explicitly.
   *
   * The step order is load-bearing, and so is the validation: `nearestFit`
   * NEVER reports failure — when nothing fits it falls back to the clamped
   * target, which may overlap. So this must decide for itself:
   *   1. grow in place if the new footprint fits at the current origin
   *      (the common case; avoids the tile teleporting across the desktop),
   *   2. else nearestFit, but only if `fits()` confirms it,
   *   3. else refuse and keep the previous size.
   * All steps resolve against displayDesktopItems(), the same rule
   * moveDesktopItem follows, so resize and move agree at narrow widths.
   */
  resizeDesktopItem(id: string, cols: number, rows: number) {
    const it = this.desktopItems.find((d) => d.id === id);
    if (!it) return;
    if (it.cols === cols && it.rows === rows) return;
    const g = this.gridCols();
    const view = this.displayDesktopItems();
    const cur = view.find((v) => v.id === id);
    if (!cur) return;
    const others = view.filter((v) => v.id !== id);

    let dest: { col: number; row: number } | null = null;
    if (fits(others, cur.col, cur.row, cols, rows, g)) {
      dest = { col: cur.col, row: cur.row };
    } else {
      const n = nearestFit(others, id, cur.col, cur.row, cols, rows, g);
      // Reject nearestFit's overlapping fallback rather than trusting it.
      if (fits(others, n.col, n.row, cols, rows, g)) dest = n;
    }
    if (!dest) {
      // Reachable precisely BECAUSE step 2 validates. Presets are pre-filtered
      // to cols <= gridCols(), so this is a crowded desktop, not a bad width.
      this.notify("Not enough room for that size", "warn");
      return;
    }

    const relocated = dest.col !== cur.col || dest.row !== cur.row;
    // cols/rows always; col/row ONLY on relocation. Stored col/row is the
    // user's intent — the same separation setGridCols/layoutForWidth rely on —
    // so resizing at a narrow width must not overwrite the position the tile
    // returns to when the window widens again.
    this.desktopItems = this.desktopItems.map((d) =>
      d.id === id
        ? { ...d, cols, rows, ...(relocated ? { col: dest.col, row: dest.row } : {}) }
        : d
    );
    this.persistDesktop();
    this.emit();
  }
  removeDesktopItem(id: string) {
    this.desktopItems = this.desktopItems.filter((d) => d.id !== id);
    // Placement ids are never reused (itemSeq is seeded from the max across
    // both surfaces), so dropping the settings here is safe — otherwise deleted
    // tiles accumulate in localStorage forever.
    this.resetWidgetSettings(id);
    this.persistDesktop();
    this.emit();
  }
  /** Current logical grid width in cells; DesktopGrid keeps this fresh on resize. */
  gridCols() {
    return this._gridCols || 12;
  }
  setGridCols(n: number) {
    // Called on EVERY window-resize tick — bail unless the logical width really
    // changed, and never emit for an unchanged width.
    //
    // Note this deliberately does NOT rewrite stored positions. Clamping tiles
    // into a narrow viewport and persisting that is lossy: widening again can't
    // recover the arrangement, so resizing back and forth walks the desktop
    // apart. Stored col/row is the user's intent (written only on drop);
    // fitting it to the current width is a render concern — see
    // layoutForWidth(), which DesktopGrid applies at paint time.
    if (n === this._gridCols) return;
    this._gridCols = n;
    this.emit();
  }

  /**
   * Manifest for a module id, but only if the server actually served it to this
   * account. `this.packages` IS the server-filtered list (routes/api.ts applies
   * packageVisible), so anything absent from it is something this account may
   * not see — a stale tile from a previous account, or a hand-edited
   * localStorage entry. Either way it must not render.
   *
   * Returns undefined until the first fetch lands (`packages` empty), and every
   * caller treats "not loaded yet" as "render nothing new", never as "erase" —
   * nothing here writes to storage, so a slow fetch cannot destroy a layout.
   */
  manifestIfVisible(moduleId: string, kind?: ModuleManifest["kind"]): ModuleManifest | undefined {
    for (const p of this.packages) {
      if (p.state !== "enabled") continue;
      const m = p.manifest;
      if ((m.id || p.id) !== moduleId) continue;
      if (kind && m.kind !== kind) continue;
      return m;
    }
    return undefined;
  }

  /** True once /api/packages has answered for this account. */
  private packagesLoaded(): boolean {
    return this.packages.length > 0;
  }

  /**
   * Tiles as currently displayed: stored intent fitted to the live grid width,
   * minus any tile whose package this account cannot see.
   */
  displayDesktopItems(): DesktopItem[] {
    const items = this.packagesLoaded()
      ? this.desktopItems.filter((it) =>
          this.manifestIfVisible(it.moduleId, it.type === "widget" ? "widget" : "app")
        )
      : this.desktopItems;
    return layoutForWidth(items, this.gridCols());
  }

  /** Panel widgets this account may actually see — same rule as the desktop. */
  displayPanelItems(): PanelItem[] {
    if (!this.packagesLoaded()) return this.panelItems;
    return this.panelItems.filter((p) => this.manifestIfVisible(p.moduleId, "widget"));
  }

  addPanelItem(moduleId: string): string {
    const id = this.nextItemId();
    this.panelItems = [...this.panelItems, { id, moduleId }];
    this.persistPanel();
    this.emit();
    return id;
  }
  removePanelItem(id: string) {
    this.panelItems = this.panelItems.filter((p) => p.id !== id);
    this.resetWidgetSettings(id); // ids are never reused — see removeDesktopItem
    this.persistPanel();
    this.emit();
  }
  setPanelOpen(open: boolean) {
    this.panelOpen = open;
    this.emit();
  }
  togglePanel() {
    this.panelOpen = !this.panelOpen;
    this.emit();
  }

  /**
   * Dock shows pinned apps only.
   * Full catalog: Files → Applications / Spotlight / App Manager.
   */
  dockApps(): ModuleManifest[] {
    const byId = new Map<string, ModuleManifest>();
    for (const p of this.packages) {
      if (p.state !== "enabled" || p.manifest.kind !== "app") continue;
      byId.set(p.manifest.id || p.id, p.manifest);
    }
    const out: ModuleManifest[] = [];
    for (const id of this.dockPins) {
      const m = byId.get(id);
      if (m) out.push(m);
    }
    return out;
  }
}

export const osStore = new OsStore();
